import React, { useState, useCallback, useEffect } from "react";
import { Button, Container, Spinner, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { useProfile } from "../../../Components/Hooks/UserHooks";
import {
  getAvailableContacts as onGetAvailableContacts,
  getClientDetails as onGetClientDetails,
  updateClient as onUpdateClient,
} from "../../../slices/thunks";
import ClientContracts from "./ClientContracts";
import ContractWizardV2 from "../../Contract/ContractWizardV2";
import ClientModal from "../ClientModal/ClientModal";
import ClientDetailHeader from "./ClientDetailHeader";
import ClientDetailStats from "./ClientDetailStats";
import ClientDetailsCard from "./ClientDetailsCard";
import '../css/ClientDetail.css'

// Selectors
const selectClientState = (state) => state.Clients;
const selectClientProperties = createSelector(selectClientState, (client) => ({
  availableContacts: client.availableContacts,
  clientInfo: client.clientInfo,
  contracts: client.contracts,
  additionalContact: client.additionalContact,
  loading: client.loading,
  error: client.error,
  isClientSuccess: client.isClientSuccess,
}));

const buildFullUpdatePayload = (clientInfo, overrides = {}) => ({
  id: clientInfo.id,
  buyer_type: clientInfo.buyer_type,
  name: clientInfo.name,
  surname: clientInfo.surname,
  country: clientInfo.country,
  region: clientInfo.region,
  city: clientInfo.city,
  postal_code: clientInfo.postal_code,
  address: clientInfo.address,
  profession: clientInfo.profession,
  passport: clientInfo.passport,
  social_security_number: clientInfo.social_security_number,
  social_security_series: clientInfo.social_security_series,
  mobile_phone_number: clientInfo.mobile_phone_number,
  land_phone_number: clientInfo.land_phone_number,
  personal_email: clientInfo.personal_email,
  business_email: clientInfo.business_email,
  legal_company_name: clientInfo.legal_company_name,
  legal_fiscal_code: clientInfo.legal_fiscal_code,
  legal_unique_registration_code: clientInfo.legal_unique_registration_code,
  legal_trade_register_number: clientInfo.legal_trade_register_number,
  legal_vat_payer: clientInfo.legal_vat_payer,
  iban_ron: clientInfo.iban_ron,
  iban_euro: clientInfo.iban_euro,
  ...overrides,
});

const ClientDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    availableContacts,
    clientInfo,
    contracts,
    additionalContact,
    loading,
    error,
  } = useSelector(selectClientProperties);

  const { userProfile } = useProfile();

  const [contractModal, setContractModal] = useState(false);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [unlinkModalOpen, setUnlinkModalOpen] = useState(false);
  const [currentClientId, setCurrentClientId] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [optimisticAdditionalContact, setOptimisticAdditionalContact] = useState(null);
  const [isContactOperationPending, setIsContactOperationPending] = useState(false);

  const displayAdditionalContact =
    optimisticAdditionalContact !== null ? optimisticAdditionalContact : additionalContact;

  const isLoadingDifferentClient = (loading && isInitialLoad) || id !== currentClientId;

  const isViewer = userProfile?.user?.groups?.includes("Viewer") || false;
  const isNaturalPerson = clientInfo?.buyer_type === "NATURAL";
  const inProgressContractsCount = (contracts || []).filter((c) => {
    const total = c.total_contract_value || 0;
    const paid = c.total_cashed || 0;
    return total > 0 && paid > 0 && paid < total;
  }).length;

  useEffect(() => {
    if (id && id !== currentClientId) {
      setIsInitialLoad(true);
      dispatch(onGetClientDetails(id));
      setCurrentClientId(id);
    }
  }, [id, dispatch, currentClientId]);

  useEffect(() => {
    if (clientInfo && !loading && !error && id === currentClientId) {
      setIsInitialLoad(false);
      if (!isContactOperationPending) {
        setOptimisticAdditionalContact(null);
      }
    }
  }, [clientInfo, loading, error, id, currentClientId, isContactOperationPending]);

  useEffect(() => {
    if (!id) return;
    dispatch(onGetAvailableContacts(id)).catch((err) => {
      console.error("Error loading clients:", err);
    });
  }, [dispatch, id]);

  const handleContractModal = useCallback(() => setContractModal(true), []);
  const handleCloseContractModal = useCallback(() => setContractModal(false), []);

  const handleOpenEdit = useCallback(() => setClientModalOpen(true), []);
  const handleCloseEdit = useCallback(() => setClientModalOpen(false), []);
  const handleClientUpdated = useCallback(() => {
    dispatch(onGetClientDetails(id));
  }, [dispatch, id]);

  const toggleUnlinkModal = useCallback(() => setUnlinkModalOpen((v) => !v), []);

  const handleUnlinkAdditionalContact = useCallback(async () => {
    setIsContactOperationPending(true);
    setOptimisticAdditionalContact(null);
    setUnlinkModalOpen(false);

    try {
      await dispatch(onUpdateClient(buildFullUpdatePayload(clientInfo, { additional_contact1: null }))).unwrap();
      await dispatch(onGetClientDetails(id)).unwrap();
    } catch (err) {
      console.error("Error unlinking contact:", err);
      setOptimisticAdditionalContact(additionalContact);
    } finally {
      setIsContactOperationPending(false);
    }
  }, [dispatch, clientInfo, additionalContact, id]);

  const handleLinkExisting = useCallback(
    async (contactId) => {
      if (!contactId) return;
      setIsContactOperationPending(true);

      const selectedContact = availableContacts.find((c) => String(c.id) === String(contactId));
      if (selectedContact) {
        setOptimisticAdditionalContact(selectedContact);
      }

      try {
        await dispatch(
          onUpdateClient(buildFullUpdatePayload(clientInfo, { additional_contact1: contactId }))
        ).unwrap();
        await dispatch(onGetClientDetails(id)).unwrap();
      } catch (err) {
        console.error("Error linking contact:", err);
        setOptimisticAdditionalContact(null);
      } finally {
        setIsContactOperationPending(false);
      }
    },
    [dispatch, availableContacts, clientInfo, id]
  );

  if (isLoadingDifferentClient) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
            <Spinner color="primary" />
            <span className="ms-2">Loading client details...</span>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <i className="ri-error-warning-line fs-1 text-danger mb-3 d-block"></i>
            <h5>Error Loading Client</h5>
            <p className="text-muted">{error}</p>
            <Button color="primary" onClick={() => dispatch(onGetClientDetails(id))}>
              Try Again
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  if (!clientInfo) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <i className="ri-user-line fs-1 text-muted mb-3 d-block"></i>
            <h5>No Client Data Found</h5>
            <p className="text-muted">The requested client could not be found.</p>
            <Button color="primary" onClick={() => navigate("/financial/clients")}>
              Back to Clients
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid style={{ maxWidth: "100%" }}>
        <ClientDetailHeader
          clientInfo={clientInfo}
          isNaturalClient={isNaturalPerson}
          isViewer={isViewer}
          onEdit={handleOpenEdit}
          onNewContract={handleContractModal}
        />

        <ClientDetailStats contracts={contracts} />

        <ClientDetailsCard
          clientInfo={clientInfo}
          isNaturalClient={isNaturalPerson}
          isViewer={isViewer}
          onEdit={handleOpenEdit}
          additionalContact={displayAdditionalContact}
          availableContacts={availableContacts}
          isContactOperationPending={isContactOperationPending}
          onLinkExisting={handleLinkExisting}
          onUnlinkRequest={toggleUnlinkModal}
        />

        <div style={{ display: "flex", flexDirection: "column", gap: 14, width: "100%" }}>
          <div className="d-flex flex-wrap align-items-center justify-content-between" style={{ padding: "0 2px" }}>
            <div className="d-flex align-items-baseline gap-2">
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 650, letterSpacing: -0.2 }}>Contracts</h2>
              {contracts && contracts.length > 0 && (
                <span style={{ fontSize: 13, color: "#8a8a96" }}>
                  {contracts.length} total
                  {inProgressContractsCount > 0 && ` · ${inProgressContractsCount} in progress`}
                </span>
              )}
            </div>
          </div>
          <ClientContracts contracts={contracts} />
        </div>
      </Container>

      <ClientModal
        mode="edit"
        client={clientInfo}
        show={clientModalOpen}
        onCloseClick={handleCloseEdit}
        onClientUpdated={handleClientUpdated}
      />

      <ContractWizardV2
        isOpen={contractModal}
        onClose={handleCloseContractModal}
        clientDetails={clientInfo}
        onCreated={handleClientUpdated}
      />

      <Modal isOpen={unlinkModalOpen} toggle={toggleUnlinkModal} centered>
        <ModalHeader toggle={toggleUnlinkModal}>Unlink Additional Contact</ModalHeader>
        <ModalBody>
          <div className="text-center">
            <i className="ri-user-unfollow-line fs-1 text-warning mb-3 d-block"></i>
            <p>Are you sure you want to unlink this contact? This action cannot be undone.</p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={toggleUnlinkModal}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleUnlinkAdditionalContact} disabled={isContactOperationPending}>
            {isContactOperationPending ? (
              <>
                <Spinner size="sm" className="me-1" />
                Unlinking...
              </>
            ) : (
              <>
                <i className="ri-delete-bin-line me-1"></i>Unlink
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ClientDetail;
